import Papa from 'papaparse';
import type { ClientRecord } from './types';
import { clientRecordSchema } from './schemas';

export interface CsvParseResult {
  ok: ClientRecord[];
  errors: Array<{ rowIndex: number; clientIdHint: string; message: string }>;
}

const MAX_ROWS = 50_000;

export function parseCsv(text: string): CsvParseResult {
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transform: (v) => (typeof v === 'string' ? v.trim() : v),
  });

  const ok: ClientRecord[] = [];
  const errors: CsvParseResult['errors'] = [];

  const rows = parsed.data.slice(0, MAX_ROWS);

  rows.forEach((row, i) => {
    const result = clientRecordSchema.safeParse(row);
    if (result.success) {
      ok.push(result.data);
    } else {
      errors.push({
        rowIndex: i + 2, // header is row 1
        clientIdHint: typeof row.client_id === 'string' ? row.client_id : '',
        message: result.error.issues
          .map((iss) => `${iss.path.join('.')}: ${iss.message}`)
          .join('; '),
      });
    }
  });

  return { ok, errors };
}

/**
 * CSV-injection-safe cell encoder. Prefixes cells starting with =, +, -, @, |
 * with a single quote so the value cannot be interpreted as a formula by
 * spreadsheet software.
 */
const FORMULA_PREFIX = /^[=+\-@|\t\r]/;

export function safeCsvCell(value: unknown): string {
  const str =
    value === null || value === undefined
      ? ''
      : typeof value === 'boolean'
        ? value
          ? 'TRUE'
          : 'FALSE'
        : String(value);
  const safe = FORMULA_PREFIX.test(str) ? `'${str}` : str;
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}
